import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LabpartnerService } from 'src/app/services/labpartner.service';
import { ICCassetteGroup } from 'src/app/services/labpartner.service.model';

export interface ICImagesDialogComponentData {
  cassetteGroup: ICCassetteGroup;
}

@Component({
  selector: 'ic-images-dialog',
  templateUrl: './ic-images-dialog.component.html',
  styleUrls: ['./ic-images-dialog.component.scss'],
  standalone: false,
})
export class ICImagesDialogComponent implements OnInit {
  imageResults: {
    testResultId: string;
    imageSetNumber: string;
    cassetteNumber: string;
    iterationOrder: number;
    delay: number;
    dark?: SafeUrl | null;
    light?: SafeUrl | null;
    cc?: SafeUrl | null;
  }[] = [];

  constructor(
    public dialogRef: MatDialogRef<ICImagesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ICImagesDialogComponentData,
    private apiService: LabpartnerService,
    private sanitizer: DomSanitizer,
  ) {}

  private getImageSetNumber(testResultId?: string): string {
    if (!testResultId) {
      return '-';
    }

    const segments = testResultId.split('_');
    return segments.length > 0 ? segments[segments.length - 1] : '-';
  }

  ngOnInit(): void {
    if (this.data.cassetteGroup?.testResults?.length) {
      const imageRequests = this.data.cassetteGroup.testResults.map(tr =>
        forkJoin([
          this.apiService.icDownloadImage(tr.convertedImages.imagePathDark).pipe(
            catchError(err => {
              console.error('Dark image failed', err);
              return of(null);
            }),
          ),
          this.apiService.icDownloadImage(tr.convertedImages.imagePathLight).pipe(
            catchError(err => {
              console.error('Light image failed', err);
              return of(null);
            }),
          ),
          this.apiService.icDownloadImage(tr.convertedImages.imagePathCc).pipe(
            catchError(err => {
              console.error('CC image failed', err);
              return of(null);
            }),
          ),
        ]).pipe(
          // After downloading, map the result to include sanitized URLs and delay info
          catchError(() => of([null, null, null])),
        ),
      );

      forkJoin(imageRequests).subscribe(results => {
        this.imageResults = results
          .map((res, index) => {
            const [dark, light, cc] = res;
            const tr = this.data.cassetteGroup.testResults[index];
            return {
              testResultId: tr.testResultId || '-',
              imageSetNumber: this.getImageSetNumber(tr.testResultId),
              cassetteNumber: tr.imageCapture?.casseteNumber || '-',
              iterationOrder: tr.imageCapture?.iterationOrder || 0,
              delay: tr.imageCapture.delayS ? tr.imageCapture.delayS : 0,
              dark: dark
                ? this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(dark))
                : null,
              light: light
                ? this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(light))
                : null,
              cc: cc
                ? this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(cc))
                : null,
            };
          })
          .sort((left, right) => {
            const leftImageSet = Number(left.imageSetNumber);
            const rightImageSet = Number(right.imageSetNumber);

            if (Number.isFinite(leftImageSet) && Number.isFinite(rightImageSet)) {
              return leftImageSet - rightImageSet;
            }

            return left.testResultId.localeCompare(right.testResultId);
          });
      });
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
